import { useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    makeStyles,
    Text,
} from '@fluentui/react-components';
import { ArrowReset20Regular, Delete20Regular, DeleteRegular } from '@fluentui/react-icons';
import { CaseDocument, UserAccount } from '../../../../data/types';
import { Column, Row } from '../../../common';
import {
    CustomDataTable,
    CustomDataTableColumn,
    CustomTableCellLayout,
} from '../../../controls/CustomDataTable/CustomDataTable';
import { useCustomDataTableFeatures } from '../../../controls/CustomDataTable/useCustomDataTableFeatures';
import { ConfirmationDialog } from '../../../dialogs/ConfirmationDialog';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { useUtilities } from '../../../../utilities/useUtilities';

type DocumentPurgeRestoreDialogProps = {
    open: boolean;
    onOpenChange: (isOpen: boolean) => void;
    deletedDocs: CaseDocument[];
    onRestoreDocs: (docs: CaseDocument[]) => Promise<void>;
    onPurgeDocs: (docs: CaseDocument[]) => Promise<void>;
};

enum ColumnName {
    name = 'name',
    description = 'description',
    deletedBy = 'deletedBy',
    deletedOn = 'deletedOn',
    size = 'size',
}

const useStyles = makeStyles({
    surface: {
        width: 'min(95vw, 980px)',
        maxWidth: '980px',
    },
    tableWrap: {
        height: '28rem',
    },
});

export const DocumentPurgeRestoreDialog = (props: DocumentPurgeRestoreDialogProps) => {
    const { open, onOpenChange, deletedDocs, onRestoreDocs, onPurgeDocs } = props;

    // Hooks
    const { primaryAccount } = useAccountState();
    const { accountUsers } = (primaryAccount as UserAccount) ?? {};
    const utils = useUtilities();
    const tableFeatures = useCustomDataTableFeatures();

    // State
    const [selectedDocs, setSelectedDocs] = useState<CaseDocument[]>([]);

    // Styles
    const styles = useStyles();

    const columns = useMemo<CustomDataTableColumn<CaseDocument>[]>(() => {
        return [
            {
                id: ColumnName.name,
                header: 'Name',
                compare: (a, b) => a.name?.localeCompare(b.name || '') || 0,
                renderCell: (doc) => {
                    return (
                            <Text>{doc.name}</Text>
                    );
                },
                defaultWidth: 280,
                minWidth: 140,
            },
            {
                id: ColumnName.description,
                header: 'Description',
                compare: (a, b) => a.description?.localeCompare(b.description || '') || 0,
                renderCell: (doc) => {
                    return (
                            <Text>{doc.description || '-'}</Text>
                    );
                },
                defaultWidth: 260,
                minWidth: 140,
            },
            {
                id: ColumnName.deletedBy,
                header: 'Deleted By',
                compare: (a, b) => {
                    const aUser = utils.getUsersName(a.lifecycle?.deletedBy || '', accountUsers);
                    const bUser = utils.getUsersName(b.lifecycle?.deletedBy || '', accountUsers);
                    return aUser.localeCompare(bUser);
                },
                renderCell: (doc) => {
                    return (
                            <Text>{utils.getUsersName(doc.lifecycle?.deletedBy || '', accountUsers) || '-'}</Text>
                    );
                },
                defaultWidth: 180,
                minWidth: 120,
            },
            {
                id: ColumnName.deletedOn,
                header: 'Deleted On',
                compare: (a, b) => {
                    const aVal = new Date(a.lifecycle?.deletedOn || 0).valueOf();
                    const bVal = new Date(b.lifecycle?.deletedOn || 0).valueOf();
                    return aVal - bVal;
                },
                renderCell: (doc) => {
                    return (
                            <Text>{doc.lifecycle?.deletedOn ? new Date(doc.lifecycle.deletedOn).toLocaleString() : '-'}</Text>
                    );
                },
                defaultWidth: 180,
                minWidth: 140,
            },
            {
                id: ColumnName.size,
                header: 'Size',
                compare: (a, b) => (a.size || 0) - (b.size || 0),
                renderCell: (doc) => {
                    return (
                            <Text>{utils.formatSize(doc.size || 0)}</Text>
                    );
                },
                defaultWidth: 120,
                minWidth: 90,
            },
        ];
    }, [accountUsers, utils]);

    const clearSelection = () => {
        setSelectedDocs([]);
        tableFeatures.clearSelection();
    };

    const onConfirmRestore = async () => {
        if (!selectedDocs.length) {
            return;
        }

        await onRestoreDocs(selectedDocs);
        clearSelection();
    };

    const onConfirmPurge = async () => {
        if (!selectedDocs.length) {
            return;
        }

        await onPurgeDocs(selectedDocs);
        clearSelection();
    };

    const onDialogOpenChange = (_ev: unknown, data: { open: boolean }) => {
        if (!data.open) {
            clearSelection();
        }

        onOpenChange(data.open);
    };

    return (
        <Dialog open={open} onOpenChange={onDialogOpenChange}>
            <DialogSurface className={styles.surface}>
                <DialogBody>
                    <DialogTitle>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                <Delete20Regular />
                            </Column>
                            <Column>Deleted Documents</Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        <Column gap='small'>
                            <Text>
                                Select one or more deleted documents to restore them to the case or permanently purge them.
                            </Text>
                            <Text size={200}>{deletedDocs.length} deleted documents</Text>
                            <Column className={styles.tableWrap}>
                                <CustomDataTable<CaseDocument>
                                    items={deletedDocs}
                                    rowId='docId'
                                    columns={columns}
                                    onSelectionChanged={setSelectedDocs}
                                    showActions={false}
                                    bodyHeight='26rem'
                                    sortable
                                    {...tableFeatures.tableProps}
                                />
                            </Column>
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            appearance='primary'
                            icon={<ArrowReset20Regular />}
                            onClick={onConfirmRestore}
                            disabled={!selectedDocs.length}
                            data-testid='documents-restore-selected-btn'
                        >
                            Restore Selected
                        </Button>
                        <ConfirmationDialog
                            title='Confirm Purge'
                            icon={<DeleteRegular />}
                            text={`Permanently purge ${selectedDocs.length} selected document(s)? This action cannot be undone.`}
                            onConfirm={onConfirmPurge}
                            confirmButtonText='Purge'
                            cancelButtonText='Cancel'
                        >
                            <Button
                                appearance='secondary'
                                icon={<DeleteRegular />}
                                disabled={!selectedDocs.length}
                                data-testid='documents-purge-selected-btn'
                            >
                                Purge Selected
                            </Button>
                        </ConfirmationDialog>
                        <Button appearance='secondary' onClick={() => onOpenChange(false)} data-testid='documents-close-dialog-btn'>
                            Close
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
